// Delta sync of the tenant snapshot: manifest → base → remove → snapshot batches → finalize.
// Single-flight; each batch is one rw transaction, so an interrupted sync is
// resumed by simply running again (the diff recomputes what is still missing).
import { fetchJson, ApiAuthError } from "../utils/apiClient";
import { openOfflineDb, getMeta, setMeta } from "./db";
import { diffManifest, chunkArray } from "./manifestDiff";
import { getRememberedTenant } from "./envStore";
import { isEncrypted, hasKeys, sealRows } from "./secure.js";

export const SNAPSHOT_BATCH_SIZE = 25;

let state = {
  tenant: null,
  inFlight: false,
  phase: "idle",          // idle | manifest | base | remove | snapshot | index | done | error
  done: 0,
  total: 0,
  lastSync: null,
  lastResult: null,        // ok | noop | auth | offline | error
  lastError: null,
  docCount: 0,
  chunkCount: 0,
  indexState: "missing",   // missing | stale | building | fresh (step 4)
};
const stateListeners = new Set();
const eventListeners = new Set();
let inflight = null;

function emit(patch) {
  state = { ...state, ...patch };
  stateListeners.forEach((fn) => { try { fn(state); } catch { /* ignore */ } });
}
function emitEvent(evt) {
  eventListeners.forEach((fn) => { try { fn(evt); } catch { /* ignore */ } });
}

export function getSyncState() { return state; }
export function subscribe(fn) { stateListeners.add(fn); fn(state); return () => stateListeners.delete(fn); }
export function subscribeEvents(fn) { eventListeners.add(fn); return () => eventListeners.delete(fn); }

// Hook for step 4: rebuild the search index after data changed.
let indexBuilder = null;
export function setIndexBuilder(fn) { indexBuilder = fn; }

// Load persisted facts (last sync, counts) so the UI is right before any network.
export async function initSyncState(tenant) {
  const db = openOfflineDb(tenant);
  if (!db) { emit({ tenant: null, lastSync: null, docCount: 0, chunkCount: 0 }); return state; }
  try {
    const [lastSync, indexState, docCount, chunkCount] = await Promise.all([
      getMeta(db, "lastSync"), getMeta(db, "indexState", "missing"), db.documents.count(), db.chunks.count(),
    ]);
    emit({ tenant, lastSync, indexState, docCount, chunkCount });
  } catch (err) {
    emit({ tenant, lastError: err?.message || String(err) });
  }
  return state;
}

export function resetSyncState() {
  emit({ inFlight: false, phase: "idle", done: 0, total: 0, lastSync: null, lastResult: null, lastError: null, docCount: 0, chunkCount: 0, indexState: "missing" });
}

export function syncNow(opts = {}) {
  if (inflight) return inflight;
  inflight = run(opts).finally(() => { inflight = null; });
  return inflight;
}

async function run({ reason = "manual", batchSize = SNAPSHOT_BATCH_SIZE } = {}) {
  const startedAt = Date.now();
  const tenant = getRememberedTenant();
  const db = openOfflineDb(tenant);
  if (!db) {
    emit({ phase: "error", lastResult: "error", lastError: "kein Tenant bekannt" });
    return { status: "error", error: "no tenant" };
  }
  if (isEncrypted() && !hasKeys()) {
    // Encrypted copy without a key (server key unavailable): never write plaintext.
    emit({ phase: "error", lastResult: "error", lastError: "Offline-Schlüssel nicht verfügbar" });
    return { status: "error", error: "no key" };
  }
  emit({ tenant, inFlight: true, phase: "manifest", done: 0, total: 0, lastError: null });

  const fail = (err) => {
    const auth = err instanceof ApiAuthError;
    const network = !auth && (err?.name === "TypeError" || err?.name === "AbortError");
    const status = auth ? "auth" : network ? "offline" : "error";
    emit({ inFlight: false, phase: "error", lastResult: status, lastError: err?.message || String(err) });
    emitEvent({ type: "error", status, error: err, reason });
    return { status, error: err };
  };

  let manifest;
  try {
    manifest = await fetchJson("/api/offline/manifest", { timeoutMs: 30000 });
  } catch (err) { return fail(err); }
  if (manifest?.tenant && manifest.tenant !== tenant) {
    return fail(new Error(`Manifest gehört zu Tenant ${manifest.tenant}, erwartet ${tenant}`));
  }

  let local;
  try {
    local = await db.documents.toArray();
  } catch (err) { return fail(err); }
  const schemaChanged = (await getMeta(db, "schemaVersion")) !== manifest.schemaVersion;
  const diff = schemaChanged
    ? { added: manifest.docs.map((d) => d.docId), changed: [], removed: local.map((d) => d.docId).filter((id) => !manifest.docs.some((d) => d.docId === id)), unchanged: 0 }
    : diffManifest(local, manifest.docs);

  try {
    emit({ phase: "base" });
    const base = await fetchJson("/api/offline/base", { timeoutMs: 30000 });
    // Sealed outside the transaction: WebCrypto awaits would end an IndexedDB transaction.
    // `order` preserves the server's (Qdrant scroll) order — Dexie would otherwise sort ids as strings ("10" < "2").
    const partRows = await sealRows("parts", (base.parts || []).map((p, order) => ({ id: String(p.id), name: p.name, parent_id: p.parent_id || "", order })));
    const typeRows = await sealRows("documentTypes", (base.documentTypes || []).map((type, order) => ({ type, order })));
    await db.transaction("rw", db.parts, db.documentTypes, async () => {
      await db.parts.clear();
      await db.parts.bulkPut(partRows);
      await db.documentTypes.clear();
      await db.documentTypes.bulkPut(typeRows);
    });

    if (diff.removed.length) {
      emit({ phase: "remove" });
      // The encrypted copy has no sections table (nothing reads it).
      const tables = isEncrypted() ? [db.documents, db.chunks] : [db.documents, db.sections, db.chunks];
      await db.transaction("rw", tables, async () => {
        await db.documents.bulkDelete(diff.removed);
        if (!isEncrypted()) await db.sections.where("docId").anyOf(diff.removed).delete();
        await db.chunks.where("docId").anyOf(diff.removed).delete();
      });
      emitEvent({ type: "docs-removed", docIds: diff.removed });
    }

    const todo = [...diff.added, ...diff.changed];
    emit({ phase: "snapshot", done: 0, total: todo.length });
    let done = 0;
    for (const batch of chunkArray(todo, batchSize)) {
      const snap = await fetchJson("/api/offline/snapshot", { method: "POST", body: { docIds: batch }, timeoutMs: 120000 });
      const docRows = await sealRows("documents", snap.documents || []);
      const chunkRows = await sealRows("chunks", snap.chunks || []);
      const tables = isEncrypted() ? [db.documents, db.chunks] : [db.documents, db.sections, db.chunks];
      await db.transaction("rw", tables, async () => {
        if (!isEncrypted()) await db.sections.where("docId").anyOf(batch).delete();
        await db.chunks.where("docId").anyOf(batch).delete();
        await db.documents.bulkPut(docRows);
        if (!isEncrypted()) await db.sections.bulkPut(snap.sections || []);
        await db.chunks.bulkPut(chunkRows);
      });
      done += batch.length;
      emit({ done });
    }

    const changedAnything = todo.length > 0 || diff.removed.length > 0 || schemaChanged;
    const indexState = await getMeta(db, "indexState", "missing");
    if (indexBuilder && (changedAnything || indexState !== "fresh")) {
      emit({ phase: "index" });
      await setMeta(db, "indexState", "building");
      try {
        await indexBuilder({ tenant, db, onProgress: (p) => emit({ done: p.done, total: p.total }) });
        await setMeta(db, "indexState", "fresh");
      } catch (err) {
        console.warn("[offline] index build failed:", err?.message || err);
        await setMeta(db, "indexState", "stale");
      }
    } else if (changedAnything) {
      await setMeta(db, "indexState", "stale");
    }

    const now = new Date().toISOString();
    await setMeta(db, "lastSync", now);
    await setMeta(db, "lastManifestAt", manifest.generatedAt || now);
    await setMeta(db, "schemaVersion", manifest.schemaVersion);
    const [docCount, chunkCount, finalIndexState] = await Promise.all([db.documents.count(), db.chunks.count(), getMeta(db, "indexState", "missing")]);
    const result = {
      status: changedAnything ? "ok" : "noop",
      added: diff.added.length, changed: diff.changed.length, removed: diff.removed.length,
      durationMs: Date.now() - startedAt, reason,
    };
    emit({ inFlight: false, phase: "done", lastSync: now, lastResult: result.status, docCount, chunkCount, indexState: finalIndexState });
    emitEvent({ type: "done", result });
    return result;
  } catch (err) {
    return fail(err);
  }
}
